import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../apiServices';
import { apiClient } from '../apiServices/apiClient';
import { User, AuthResponse, LoginRequest } from '../models';

interface AuthContextType {
  currentUser: User | null;
  authToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; errors?: string[] }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
  fetchUserDetails: () => Promise<User>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('current-user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    return user;
  });
  const [authToken, setAuthToken] = useState<string | null>(authService.getStoredToken());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const login = async (email: string, password: string): Promise<{ success: boolean; errors?: string[] }> => {
    try {
      const loginRequest: LoginRequest = {
        email,
        password,
        rememberMe: false
      };

      const authResponse: AuthResponse = await authService.login(loginRequest);
      setAuthToken(authResponse.token);
      setCurrentUser(authResponse.user);
      authService.storeToken(authResponse.token);
      authService.storeRefreshToken(authResponse.refreshToken);
      localStorage.setItem('current-user', JSON.stringify(authResponse.user));
      
      
      return { success: true };
    } catch (error) {
      return { success: false, errors: ['Помилка підключення до сервера'] };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (authToken) {
        await authService.logout();
      }
    } catch (error) {
    } finally {
      setAuthToken(null);
      setCurrentUser(null);
      authService.removeToken();
      authService.removeRefreshToken();
      localStorage.removeItem('current-user');
    }
  };

  const checkAuth = async (): Promise<boolean> => {
    if (!authToken) {
      return false;
    }

    // Just check if token is valid by trying to refresh it
    // Don't make /me request here - it will be made only on profile page
    try {
      const authResponse = await authService.refreshToken();
      setAuthToken(authResponse.token);
      setCurrentUser(authResponse.user);
      localStorage.setItem('current-user', JSON.stringify(authResponse.user));
      return true;
    } catch (refreshError) {
      logout();
      return false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const authResponse = await authService.refreshToken();
      setAuthToken(authResponse.token);
      setCurrentUser(authResponse.user);
      authService.storeRefreshToken(authResponse.refreshToken);
      localStorage.setItem('current-user', JSON.stringify(authResponse.user));
      
      
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  const fetchUserDetails = async (): Promise<User> => {
    try {
      const response = await authService.me();
      const user = (response as any).user || response;
      setCurrentUser(user);
      localStorage.setItem('current-user', JSON.stringify(user));
      return user;
    } catch (error) {
      // If /me fails, try to refresh token and get user from there
      try {
        const authResponse = await authService.refreshToken();
        setAuthToken(authResponse.token);
        setCurrentUser(authResponse.user);
        localStorage.setItem('current-user', JSON.stringify(authResponse.user));
        return authResponse.user;
      } catch (refreshError) {
        logout();
        throw refreshError;
      }
    }
  };

  useEffect(() => {
    apiClient.setAuthToken(authToken);
  }, [authToken]);

  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      authService.initializeAuth();
      
      if (authToken) {
        await checkAuth();
      }
      setIsLoading(false);
      
    };

    initAuth();
  }, []);


  const value: AuthContextType = {
    currentUser,
    authToken,
    isLoading,
    login,
    logout,
    checkAuth,
    refreshToken,
    fetchUserDetails
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


